import { Check, Close } from "@mui/icons-material";
import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { Button, ErrorSummary, UploadStatusBox } from "components";
import {
	MotionOutlinedButtonStyle,
	MotionPrimaryButtonStyle,
} from "lib/styles/button";

interface submitStatusScreensProps {
	status: "NotAsked" | "Loading" | "Done";
	data: unknown;
	error: any;
	loadingTitle: string;
	successTitle: string;
	errorTitle?: string;
	onDashboard: () => void;
	onStartOver?: () => void;
	onCreateNew?: () => void;
}

export function SubmitStatusScreens({
	status,
	data,
	error,
	loadingTitle,
	successTitle,
	errorTitle = "Error Processing Containment",
	onDashboard,
	onStartOver,
	onCreateNew,
}: submitStatusScreensProps): React.JSX.Element | null {
	const theme = useTheme();

	if (status === "Loading") {
		return (
			<UploadStatusBox variant="loading" icon={null} iconColor="transparent">
				<Typography variant="h4" align="center">
					{loadingTitle}
				</Typography>
				<LinearProgress sx={{ height: 16, borderRadius: 8, mt: 3 }} />
			</UploadStatusBox>
		);
	}

	if (status === "Done" && data) {
		return (
			<UploadStatusBox
				variant="success"
				icon={
					<Check
						sx={{
							fontSize: 64,
							color: theme.palette.success.main,
							strokeWidth: 2,
							stroke: "currentColor",
						}}
					/>
				}
				iconColor={theme.palette.success.main}
			>
				<Typography
					data-test-selector="containment-success-title"
					variant="h4"
					color="success.main"
					align="center"
				>
					{successTitle}
				</Typography>
				<Box display="flex" justifyContent="center" mt={6} gap={3}>
					<Button
						data-test-selector="containment-success-action-dashboard"
						variant="outlined"
						color="primary"
						onClick={onDashboard}
						sx={MotionOutlinedButtonStyle}
					>
						View Containments
					</Button>
					{onCreateNew && (
						<Button
							variant="contained"
							color="primary"
							onClick={onCreateNew}
							sx={MotionPrimaryButtonStyle(theme)}
						>
							Create New Containment
						</Button>
					)}
				</Box>
			</UploadStatusBox>
		);
	}

	if (status === "Done" && !data) {
		const showErrList =
			error?.response?.data?.errorList &&
			!error.response.data.errorList.IsEmpty();
		return (
			<UploadStatusBox
				variant="error"
				icon={
					<Close
						sx={{
							fontSize: 64,
							color: theme.palette.error.main,
							strokeWidth: 2,
							stroke: "currentColor",
						}}
					/>
				}
				iconColor={theme.palette.error.main}
			>
				<Typography variant="h4" color="error.main" align="center">
					{errorTitle}
				</Typography>
				<Typography variant="body1" align="center">
					{showErrList
						? "We encountered an issue while processing your containment. Please review the errors below."
						: "Please try again or contact support if the issue persists."}
				</Typography>
				{showErrList && (
					<ErrorSummary
						errorList={error?.response?.data?.errorList ?? []}
					/>
				)}
				<Box display="flex" justifyContent="center" mt={6} gap={3}>
					<Button
						variant="outlined"
						color="primary"
						onClick={onDashboard}
						sx={MotionOutlinedButtonStyle}
					>
						View Containments
					</Button>
					{onStartOver && (
						<Button
							data-test-selector="containment-error-action-start-over"
							variant="contained"
							color="primary"
							onClick={onStartOver}
							sx={MotionPrimaryButtonStyle(theme)}
						>
							Start Over
						</Button>
					)}
				</Box>
			</UploadStatusBox>
		);
	}

	return null;
}
